<script setup lang="ts">
/* Imports */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import useI18NAsync from '~/composables/i18nAsync';
import HomepageFeatures from '~/components/homepage/HomepageFeatures.vue';
import HomepageBtn from '~/components/homepage/HomepageBtn.vue';
import HomepageProcesses from '~/components/homepage/HomepageProcesses.vue';
import HomepageStepper from '~/components/homepage/HomepageStepper.vue';
import HomepageStats from '~/components/homepage/HomepageStats.vue';
import HomepageSolidworksInstantQuote from '~/components/homepage/HomepageSolidworksInstantQuote.vue';
import HomepageProcessesGalerie from '~/components/homepage/HomepageProcessesGalerie.vue';
import HomepageUploadYourOwnDesign from '~/components/homepage/HomepageUploadYourOwnDesign.vue';
import HomepageDownloadSolidWorks from '~/components/homepage/HomepageDownloadSolidWorks.vue';
import HomepageGetListed from '~/components/homepage/HomepageGetListed.vue';
import HomepageWhyJoinAsServiceProvider from '~/components/homepage/HomepageWhyJoinAsServiceProvider.vue';
import HomepageExploreServiceProvider from '~/components/homepage/HomepageExploreServiceProvider.vue';
import HomepageBottomLinks from '~/components/homepage/HomepageBottomLinks.vue';
import HomepageSocial from '~/components/homepage/HomepageSocial.vue';
import Footer from '~/components/Footer.vue';
import UploadComponent from '~/components/upload/Upload.vue';
import rewireRouter from '~/modules/rewire-router';

import vuLazy from '@ds/vuekit/src/components/vu-lazy.vue';

import { useTracker } from '~/composables/tracker';
import bold3DExperience from '~/composables/filters';

import type { HomePageFeature, HomePageProcess } from '~/models/homepage';
import { useTranslations } from '~/composables/translations';
import { useSettingsStore } from '~/stores/settings';
import { useCartStore } from '~/stores/cart';
import { useRouter } from 'vue-router';
import { requirejs } from '~/modules/require';
import { useAlerts } from '~/composables/alerts';
import { useMeStore } from '~/stores/me';
import { NlsHomepage } from '~/typings/nls/homepage';

/* Variables */
const { $i18n } = useTranslations('homepage');
const i18nSuspense = useI18NAsync('homepage');

const windowTop = ref(0);
const header = ref();
const content = ref();
const tracker = useTracker();
const settings = useSettingsStore();
const cart = useCartStore();
const router = useRouter();
const readyToken = ref('');
const isAddinReady = ref(false);
const { isAddinMode } = settings;
const me = useMeStore();
const token = ref();
const tokenDrive = ref();
const alert = useAlerts();

/* Computed */
const displayFileUpload = computed(
  () =>
    typeof window !== 'undefined' && window && !settings.isMobileDevice && !settings.isAddinMode,
);

const features = computed(() => [
  {
    title: 'featureIpProtection' as keyof NlsHomepage,
    icon: 'globe',
    trackerLabel: 'ip_protection_feature_clicked',
  },
  {
    title: 'featureSecureStorage' as keyof NlsHomepage,
    icon: 'folder-locked',
    trackerLabel: 'secure_storage_feature_clicked',
  },
  {
    title: 'featureCreditCardAndTransfert' as keyof NlsHomepage,
    icon: 'credit-card',
    trackerLabel: 'credit_card_feature_clicked',
  },
  {
    title: 'featurePurchaseOrder' as keyof NlsHomepage,
    icon: 'doc-log',
    trackerLabel: 'purchase_order_feature_clicked',
  },
]);

const processes = computed(() => [
  {
    name: '3d_printing',
    icon: 'manufacturing-additive',
    wiki: '3D-printing',
    trackerEvent: {
      iconLabel: '3d_printing_process_icon_clicked',
      btnLabel: '3d_printing_process_clicked',
    },
    image: 'processes_3Dprinting.webp',
    id: 'PF-1e81bc36e0676bc29f33f45c89bf7ac9',
  },
  {
    name: 'cnc_machining',
    icon: 'manufacturing-substractive',
    wiki: 'CNC-machining',
    trackerEvent: {
      iconLabel: 'cnc_machining_process_icon_clicked',
      btnLabel: 'cnc_machining_process_clicked',
    },
    image: 'processes_CNCmachining.webp',
    id: 'PF-1e81bc39b8c16c97bff9f45c89bf7ac9',
  },
  {
    name: 'sheet_metal',
    icon: 'manufacturing-forming',
    wiki: 'forming',
    trackerEvent: {
      iconLabel: 'sheet_metal_process_icon_clicked',
      btnLabel: 'sheet_metal_process_clicked',
    },
    image: 'processes_sheetMetal.webp',
    id: 'PF-1e81bc3c88696e11870df45c89bf7ac9',
  },
  {
    name: 'cutting',
    icon: 'manufacturing-cutting',
    wiki: 'cutting',
    trackerEvent: {
      iconLabel: 'cutting_process_icon_clicked',
      btnLabel: 'cutting_process_clicked',
    },
    image: 'processes_laserCutting.webp',
    id: 'PF-1e81bc3b8e406dd1a552f45c89bf7ac9',
  },
  {
    name: 'injection_molding',
    icon: 'manufacturing-molding',
    wiki: 'molding',
    trackerEvent: {
      iconLabel: 'molding_process_icon_clicked',
      btnLabel: 'molding_process_clicked',
    },
    image: 'processes_injectionMolding.webp',
    id: 'PF-1e81bc3c07b6616ea0cdf45c89bf7ac9',
  },
]);

const steps = computed(() => [
  {
    name: settings.isMobileDevice ? 'securelyUploadPartMobile' : 'securelyUploadPart',
    icon: 'cloud-upload',
  },
  {
    name: settings.isMobileDevice ? 'getInstantQuote' : 'getInstantQuoteWithBr',
    icon: 'doc-log',
  },
  {
    name: settings.isMobileDevice ? 'confirmOrderMobile' : 'confirmOrder',
    icon: 'parcel-ok',
  },
  {
    name: settings.isMobileDevice ? 'receiveYourPartMobile' : 'receiveYourPart',
    icon: 'truck-delivery',
  },
]);

/* Methods */
const clickOnFeature = (feature: HomePageFeature) => {
  tracker?.event(feature.trackerLabel, 'display', 'CLICK_');
};

const refreshScrollTop = () => {
  const headerHeight = header.value.getBoundingClientRect().height;
  windowTop.value =
    headerHeight - (window.pageYOffset + content.value.getBoundingClientRect().top - 87);
};

const addPart = () => {
  tracker?.event('get_instant_quote_clicked', 'display', 'CLICK');

  if (isAddinMode) {
    addPartToCartForAddinMode();
  } else {
    goToQuoteUpload();
  }
};

const addPartToCartForAddinMode = () => {
  if (!isAddinReady.value) {
    return alert?.show({
      text: $i18n('addin_not_ready'),
      type: 'warning',
      timeout: 5000,
    });
  }

  window._addin_publish('CART_SENT');
  window._addin_publish('REQUEST_PART');

  token.value = window._addin_subscribe('REQUEST_PART_CANCELED', () => {
    window._addin_unsubscribe(token.value);
    window._addin_unsubscribe(tokenDrive.value);
  });
  tokenDrive.value = window._addin_subscribe('DRIVE_DOCUMENTS_CANCELED', () => {
    window._addin_unsubscribe(token.value);
    window._addin_unsubscribe(tokenDrive.value);
  });
};

const goToQuoteUpload = (process?: HomePageProcess) => {
  if (process) {
    tracker?.event(process.trackerEvent.btnLabel, 'display', 'CLICK');
  }

  router.push({ name: 'QuoteUploadPart', query: { pf: process?.id } });
};

const openContactSales = () => {
  tracker?.event('contact_sales_clicked', 'display', 'CLICK');
  window.open('https://www.3ds.com/3dexperience/make/3dexperience-make-contact-sales');
};

const scrollInPage = (offset: number) => {
  const homepageMainContainer = document.querySelector('#home-page-main-container');
  if (!homepageMainContainer) {
    return;
  }

  homepageMainContainer.scroll({
    top: offset + (settings.isMobileDevice ? 357 : 610),
    left: 0,
    behavior: 'smooth',
  });
};

const scrollTo = (to: string) => {
  const toComponent: HTMLElement = document.querySelector(to) as HTMLElement;
  if (!toComponent) {
    return;
  }

  scrollInPage(toComponent.offsetTop);
};

const exploreServiceProvider = () => {
  router.push({ name: 'LabsExploration' });
};

const trackIcon = (process: HomePageProcess) => {
  tracker?.event(process.trackerEvent.iconLabel, 'display', 'CLICK');
};

const initAddinMode = async () => {
  await rewireRouter();
  await requirejs(['DS/MP3DPExternalWidget/MP3DPAddinPublicEvent']);

  readyToken.value = window._addin_subscribe('READY', () => {
    isAddinReady.value = true;

    window._addin_ready = true;
  });
  setTimeout(() => {
    isAddinReady.value = true;

    window._addin_ready = true;
  }, 15000);
};

const redirectSeller = async () => {
  const me = useMeStore();
  await me.fetchMe();

  if (me.isLoggedIn) {
    if (me.isSeller) {
      if (!me.currentShop) {
        await me.fetchMeShops();
      }

      if (me.currentShop) {
        // Boolean to show cart recup modal for seller after redirection
        me.shouldShowCartRecupModal = true;
        return router.push({
          name: 'LabOverview',
          params: { id: me.currentShop },
        });
      }
    }
  }
};

onMounted(async () => {
  if (isAddinMode) {
    requirejs<[any]>(['DS/MP3DPSettings/MP3DPRouting']).then(([MP3DPRouting]: [any]) => {
      MP3DPRouting.urlHasChanged();
    });

    if (!isAddinReady.value) {
      initAddinMode();
    }
  }

  if (isAddinMode && window._addin_remove_parts) {
    window._addin_remove_parts();
  }

  await cart.resetCart();

  // If current route is /welcome
  if (router.currentRoute.value.name !== 'Welcome') {
    redirectSeller();
  }
});

onBeforeUnmount(() => {
  if (readyToken.value) {
    window._addin_unsubscribe(readyToken.value);
  }

  if (token.value) {
    window._addin_unsubscribe(token.value);
  }
});
</script>
<template>
  <div
    id="home-page-main-container"
    class="home-page-container mp-fill-max-height custom-scroll-bar-right--no-margin"
    style="height: calc(100vh - 86px)"
    @scroll.passive="refreshScrollTop"
  >
    <!-- Homepage HEADER -->
    <div
      ref="header"
      class="home-page-header flex flex-column relative white--text align-items-center px-10px--sm px-30px"
      :style="`

        filter: brightness(${100 - windowTop / 10}%);
        webkit: brightness(${100 - windowTop / 10}%);
      `"
    >
      <div
        class="home-page-header-container home-page-content-container flex flex-row--md flex-column--sm full-width mt-30px--sm mb-20px--sm mt-70px mb-40px grid-gap-20px"
        :style="`transform: translateY(${windowTop / 10 > 60 ? 0 : windowTop / 10}px) rotateY(${
          windowTop / 250
        }deg)`"
      >
        <!-- HEADLINE -->
        <div class="flex flex-column flex-grow-1">
          <h1
            class="headline slide-right-enter text-center--sm"
            v-html="bold3DExperience('3DEXPERIENCE <b>MAKE</b>')"
          />
          <h2 class="sub-headline slide-right-enter text-center--sm my-10px">
            <i18nSuspense ikey="experienceMakeSubtitle" />
          </h2>
          <div
            class="flex flex-column--sm grid-gap-15px grid-gap-6px--sm btn-group slide-right-enter align-items-center--sm my-40px mt-30px--sm mb-20px--sm"
          >
            <HomepageBtn id="header-primary-button" @click="addPart()">
              <template v-if="isAddinMode">
                <i18nSuspense ikey="addAPart" />
              </template>
              <template v-else>
                <i18nSuspense ikey="getInstantQuote" />
              </template>
            </HomepageBtn>
            <HomepageBtn id="header-contact-sale-button" color="white" @click="openContactSales()">
              <i18nSuspense ikey="contactSales" />
            </HomepageBtn>
          </div>
          <div id="caption" class="py-10px caption slide-right-enter text-center--sm">
            <i18nSuspense ikey="homepageCaption" />
          </div>
          <HomepageFeatures
            class="mt-30px"
            :features="features"
            @feature-clicked="(e: HomePageFeature) => clickOnFeature(e)"
          />
        </div>
        <div class="white full-height hidden--xs mt-30px" id="homepage-drop-zone">
          <UploadComponent
            v-if="displayFileUpload"
            :display-upload-butons="false"
            :display-shared-parts="false"
            :display-upload-features="false"
          >
            <template #title>
              <h3 class="mt-30px title">
                <i18nSuspense ikey="startUploadingFiles" />
              </h3>
            </template>
          </UploadComponent>
        </div>
      </div>
    </div>
    <div ref="content" class="home-page-content">
      <HomepageProcesses
        class="displayed-mobile-only"
        id="homepage-processes-mobile"
        :is-mobile-device="true"
        :processes="processes"
        @click-btn="(e) => goToQuoteUpload(e)"
        @process-icon-clicked="(e) => trackIcon(e)"
      />
      <HomepageStepper :steps="steps" :is-mobile-device="settings.isMobileDevice" />
      <HomepageSolidworksInstantQuote @discover-solidworks="scrollTo('#solidworks-addin')" />
      <HomepageStats />
      <vu-lazy height="300px">
        <HomepageProcessesGalerie
          :processes="processes"
          :is-mobile-device="settings.isMobileDevice"
        />
      </vu-lazy>
      <HomepageUploadYourOwnDesign id="home-journey-content" />
      <vu-lazy height="300px" id="solidworks-addin">
        <HomepageDownloadSolidWorks />
      </vu-lazy>
      <vu-lazy height="300px" id="explore-service-provider">
        <HomepageExploreServiceProvider @explore-service-provider="exploreServiceProvider()" />
      </vu-lazy>
      <vu-lazy height="300px" id="why-join-service-provider">
        <HomepageWhyJoinAsServiceProvider />
      </vu-lazy>
      <HomepageGetListed id="get-listed" />
      <HomepageSocial id="homepage-social" />
      <HomepageBottomLinks :is-seller="me.isSeller" id="bottom-links" />
    </div>
    <Footer />
  </div>
</template>
<style lang="scss" scoped>
@use '/assets/scss/variables' as variables;

* {
  font-family: '3ds';
}

.home-page-main-container {
  overflow: auto;
}

.home-page-content-container {
  max-width: 1750px;
  margin-left: auto;
  margin-right: auto;
  width: 100%;
}

.home-page-header {
  background-position: center;
  background-size: cover;
  position: -webkit-sticky;
  position: sticky;
  top: 0;
  width: 100%;
  z-index: 0;
  background-image: url('/assets/images/homepage/top_banner/top-banner.webp');

  @include variables.for-mid {
    background-image: url('/assets/images/homepage/top_banner/top-banner_medium.webp');
  }

  @include variables.for-sm {
    background-image: url('/assets/images/homepage/top_banner/top-banner_small.webp');
  }
}

.home-page-content {
  z-index: 2;
  position: relative;
  overflow: hidden;
}

.headline {
  font-size: 60px;
  line-height: 60px;

  @include variables.for-mid {
    font-size: 56px;
    line-height: 56px;
  }

  @include variables.for-sm {
    font-size: 30px;
    line-height: 30px;
  }
}

.sub-headline {
  font-size: 30px;
  line-height: 30px;

  @include variables.for-mid {
    font-size: 30px;
    line-height: 30px;
  }
  @include variables.for-sm {
    font-size: 20px;
    line-height: 20px;
  }
}

#caption {
  font-size: 26px;
  line-height: 30px;

  @include variables.for-mid {
    font-size: 20px;
    line-height: 20px;
  }
  @include variables.for-sm {
    max-width: unset;
    font-size: 18px;
    line-height: 18px;
  }
}

#homepage-processes-absolute {
  position: absolute;
  bottom: 30px;
  left: 0;
  right: 0;
  width: 1140px;

  @include variables.for-md {
    width: 60%;
  }
}

/* DEEP CSS */
.home-page-content :deep(.homepage-max-content-container) {
  width: 100%;
  max-width: 1500px;
  margin-left: auto;
  margin-right: auto;
}

.home-page-content :deep(.title) {
  font-size: 34px;
  line-height: 34px;
  font-weight: 400;

  @include variables.for-mid {
    font-size: 28px;
    line-height: 28px;
  }
  @include variables.for-sm {
    font-size: 20px;
    line-height: 20px;
  }
}

.home-page-content :deep(.text-content) {
  font-size: 16px;
  line-height: 20px;

  @include variables.for-mid {
    font-size: 15px;
    line-height: 20px;
  }
  @include variables.for-sm {
    font-size: 14px;
    line-height: 16px;
  }
}

.home-page-content :deep(.subtitle) {
  font-size: 20px;
  line-height: 20px;
  font-weight: 300;

  @include variables.for-mid {
    font-size: 18px;
    line-height: 18px;
  }
  @include variables.for-sm {
    font-size: 16px;
    line-height: 16px;
  }
}

#homepage-drop-zone {
  width: 580px;
  border-radius: 20px;
  box-shadow: 10px 10px 30px -10px var(--grey-7);
  margin-right: 100px;

  &:deep(.drop-zone) {
    margin: 40px;
  }

  &:deep(.format-tips) {
    font-size: 13px;
    padding: 0 62px;
    margin-top: 10px;
  }

  &:deep(.upload-file-btn) {
    font-family: 'Arial';
  }

  @include variables.for-mid {
    margin-right: 0px;
  }
}
</style>
